<script setup lang="ts">
import {
  faCheck,
  faChevronDown,
  faPlus,
  faSpinner,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBrandLoading, GpBtn, GpCollapse, GpCollapsePanel } from '@gportal/uikit';
import { UploadFileInjectKey } from '@panel/plugins/symbols';
import type { UploadPlugin } from '@panel/plugins/types';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import getEnumLabel from 'gpcore-apikit/lib/getEnumLabel';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import {
  AuthenticationType,
  type BasicImage,
  type Image,
  type ImageVersion,
} from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, inject, ref, toRef, watchEffect } from 'vue';
import ProjectImageVersionsTable from './ProjectImageVersionsTable.vue';
import UploadProjectImageModal, { type UploadImageMeta } from './UploadProjectImageModal.vue';

const props = defineProps<{ image: BasicImage }>();

const emit = defineEmits<{ deleted: [] }>();

const panelModel = ref<string>();

const isLoading = ref(false);
const imageStore = useImageStore();
const projectStore = useProjectStore();
const hasError = ref(false);
const errorMessage = ref('');
const imageDetail = ref<Image>(null);
const imageVersions = ref<Array<ImageVersion>>([]);
const loadProjectImage = async (forceReload = false) => {
  if ((!forceReload && imageDetail.value == null) || forceReload) {
    isLoading.value = true;

    try {
      const response = await imageStore.getProjectImage(projectStore.projectId, props.image.id);
      imageDetail.value = response.image;
      imageVersions.value = response.image.versions.map((i) => i);
      hasError.value = false;
      errorMessage.value = '';
    } catch (error) {
      hasError.value = true;
      errorMessage.value = (error as ServiceError).message;
    } finally {
      isLoading.value = false;
    }
  }
};

const isDeleteImageModalOpened = ref(false);
const isDeletingImage = ref(false);
const { addNotification } = useNotifications();
const deleteProjectImage = async () => {
  isDeletingImage.value = true;

  try {
    await imageStore.deleteProjectImage(projectStore.projectId, props.image.id);

    isDeleteImageModalOpened.value = false;
    emit('deleted');
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isDeletingImage.value = false;
  }
};

const authenticationTypes = computed(() => {
  if (imageDetail.value == null) {
    return [];
  }

  return imageDetail.value.authenticationTypes.map((auth) => ({
    label: getEnumLabel(AuthenticationType, auth),
  }));
});

const uploadProjectImageModalOpened = ref(false);

// upload handling - for refreshing versions table
const uploadPlugin = inject<UploadPlugin<UploadImageMeta>>(UploadFileInjectKey);
const isUploading = toRef(uploadPlugin, 'isUploading');
const isUploadProgressing = toRef(uploadPlugin, 'isUploadProgressing');
const isDoneUploading = toRef(uploadPlugin, 'isDoneUploading');
const uploadError = toRef(uploadPlugin, 'error');
const uploadImageMeta = toRef(uploadPlugin, 'meta');
watchEffect(async () => {
  if (
    (isUploading.value ||
      isUploadProgressing.value ||
      isDoneUploading.value ||
      uploadError.value != null) &&
    // restrict reload to this image only
    uploadImageMeta.value != null &&
    uploadImageMeta.value.id === props.image.id
  ) {
    await loadProjectImage(true);

    if (isDoneUploading.value || uploadError.value != null) {
      // reset meta on upload stop
      uploadPlugin.meta.value = null;
    }
  }
});
</script>

<template>
  <GpCollapse v-model="panelModel" @expanded="() => loadProjectImage()" outline>
    <GpCollapsePanel :panel-key="image.id" :header-label="image.name">
      <template #header="{ isOpened, toggle, collapseId }">
        <a
          :id="collapseId"
          type="button"
          class="tw-flex tw-cursor-pointer tw-flex-wrap tw-items-center tw-border tw-border-gp-rhino tw-px-4 tw-py-2 tw-text-gp-mouse hover:tw-text-gp-mouse"
          :class="{ 'tw-rounded-t': isOpened, 'tw-rounded': !isOpened }"
          @click.prevent="toggle"
        >
          <FontAwesomeIcon
            v-if="!isLoading"
            :icon="faChevronDown"
            class="tw-mr-4"
            :class="{ 'tw-rotate-180': isOpened }"
          />

          <FontAwesomeIcon v-else :icon="faSpinner" class="tw-mr-4 tw-animate-spin" />

          <div class="tw-flex-1">
            <h2>
              {{ image.name }}
            </h2>
            <small>ID: {{ image.id }}</small>
          </div>
          <!-- image delete -->
          <GpBtn
            size="sm"
            btn-type="danger"
            outline
            label="Remove"
            :icon="faTrash"
            @click.stop="isDeleteImageModalOpened = true"
          />

          <ConfirmModal
            :show="isDeleteImageModalOpened"
            :question="`Are you sure you want to remove the project image '${image.name}'?`"
            :confirm-text="isDeletingImage ? 'Removing ...' : 'Remove'"
            confirm-type="danger"
            @close="isDeleteImageModalOpened = false"
            @confirm="deleteProjectImage"
          />
        </a>
      </template>

      <template #body>
        <GpBrandLoading v-if="isLoading" class="tw-mx-auto" message="Loading project image ..." />
        <div v-else-if="hasError" class="tw-text-center">
          <h3 class="tw-mb-2">An error occurred</h3>
          <pre>{{ errorMessage }}</pre>
        </div>
        <template v-else-if="imageDetail != null">
          <!-- general -->
          <h3 class="tw-mb-4">General</h3>

          <div class="tw-grid tw-grid-cols-4 tw-gap-4">
            <label class="tw-col-span-2 tw-font-bold tw-text-gp-rhino md:tw-col-span-1">Name</label>
            <p class="tw-col-span-2 md:tw-col-span-3">
              {{ imageDetail.name }}
            </p>

            <template v-for="auth in authenticationTypes" :key="auth">
              <label class="tw-col-span-2 tw-font-bold tw-text-gp-rhino md:tw-col-span-1">
                {{ auth.label }} Authentication
              </label>
              <p class="tw-col-span-2 tw-mb-0 tw-text-gp-green md:tw-col-span-1">
                <FontAwesomeIcon :icon="faCheck" class="tw-mr-2" />
                Enabled
              </p>
            </template>
          </div>

          <!-- versions -->
          <div class="tw-mt-8 tw-flex tw-justify-between">
            <h3>Versions</h3>

            <GpBtn
              size="sm"
              btn-type="primary"
              label="Upload"
              :icon="faPlus"
              @click="uploadProjectImageModalOpened = true"
            />
          </div>

          <ProjectImageVersionsTable
            class="tw-mt-4"
            :versions="imageVersions"
            @deleted="() => loadProjectImage(true)"
          />
        </template>

        <UploadProjectImageModal
          :show="uploadProjectImageModalOpened"
          :image="imageDetail"
          @close="uploadProjectImageModalOpened = false"
        />
      </template>
    </GpCollapsePanel>
  </GpCollapse>
</template>

<style scoped></style>
